import { jsx as _jsx } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef, } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiClient, ApiClientService, AuthContext } from 'libs.react_internal';
export const DataReportsContext = createContext({
    activeReportsTab: 0,
    setActiveReportsTab: () => { },
    drilldownLevel: 0,
    setDrilldownLevel: () => { },
    libRequestsParams: {},
    selectedTestEnv: { label: '', value: '' },
    setSelectedTestEnv: () => { },
    devEnvironments: [],
    prodEnvironments: [],
    isInitialized: false,
    isLoadingEnvironments: false,
    hasProductionEnvironments: false,
    setStudyId: () => { },
    testEnvironmentsOptions: [],
    triggerCortexLogin: () => Promise.resolve(),
    setIsCortexLoginSettled: () => { },
    fetchAuthPolicies: () => Promise.resolve(),
    authPolicies: [],
    hasPolicy: () => false,
});
export const DataReportsProvider = ({ children }) => {
    const iamClient = useApiClient(ApiClientService.IAM);
    const momClient = useApiClient(ApiClientService.MOM);
    const { entityId } = useContext(AuthContext);
    const { user } = useContext(AuthContext);
    const [drilldownLevel, setDrilldownLevel] = useState(0);
    const [activeReportsTab, setActiveReportsTab] = useState(0);
    const [selectedTestEnv, setSelectedTestEnv] = useState({ label: '', value: '' });
    const [studyId, setStudyId] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCortexLoginSettled, setIsCortexLoginSettled] = useState(false);
    const [authPolicies, setAuthPolicies] = useState([]);
    const cortexLoginInProgressRef = useRef(false);
    const [searchParams] = useSearchParams();
    const env = searchParams.get('env');
    const getEnvsData = async (isProd = false) => {
        const params = {
            withWorkflows: true,
            withCortexAccess: true,
        };
        if (isProd) {
            params.clusterTypes = 'prod';
        }
        else {
            params.clusterTypes = 'dev';
        }
        const { data: response } = await momClient.get(`/v1/entities/${entityId}/studies/${studyId}/environments`, {
            params,
        });
        return response.data;
    };
    const { data: devEnvironments = [], isLoading: isLoadingDevEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', studyId, 'environments', 'dev'],
        queryFn: () => getEnvsData(false),
        enabled: !!entityId && !!studyId,
    });
    const { data: prodEnvironments = [], isLoading: isLoadingProdEnvironments } = useQuery({
        queryKey: ['entities', entityId, 'studies', studyId, 'environments', 'prod'],
        queryFn: () => getEnvsData(true),
        enabled: !!entityId && !!studyId,
    });
    const hasProductionEnvironments = useMemo(() => prodEnvironments.length > 0, [prodEnvironments]);
    const testEnvironmentsOptions = useMemo(() => devEnvironments
        ?.filter((env) => env.deployment.type !== 'ephemeral')
        .map((env) => ({
        label: env.name,
        value: env.cortexId,
    })), [devEnvironments]);
    useEffect(() => {
        if (devEnvironments?.length > 0 || prodEnvironments?.length > 0) {
            setIsInitialized(true);
        }
    }, [devEnvironments, prodEnvironments]);
    // Reset cortex login progress when environments change
    useEffect(() => {
        cortexLoginInProgressRef.current = false;
        setIsCortexLoginSettled(false);
    }, [devEnvironments, selectedTestEnv.value, env]);
    const libRequestsParams = useMemo(() => {
        if (env === 'prod') {
            return { latest: true, status: 'PUBLISHED' };
        }
        if (!env && activeReportsTab === 1) {
            return { latest: true, status: 'PUBLISHED' };
        }
        return {};
    }, [activeReportsTab, env]);
    const isLoadingEnvironments = useMemo(() => {
        return isLoadingDevEnvironments || isLoadingProdEnvironments;
    }, [isLoadingDevEnvironments, isLoadingProdEnvironments]);
    const getCortexEndpointUrl = (env) => {
        return `${env.cortexURL}/${env.code}`;
    };
    const loginToCortex = useCallback(async (env) => {
        try {
            // First, check status to see if user is already logged into cortex
            // Note: we're using fetch to skip including md_idtoken in the request
            const response = await fetch(`${getCortexEndpointUrl(env)}/v2/accounts/status?__no_redirect__=1&expand=true`, {
                headers: {
                    'Medable-Client-Key': 'c_default_app',
                },
                credentials: 'include',
            });
            const { data } = await response.json();
            // Check if user is already logged in to cortex and if the email is the same as the user's email
            if (data?.loggedin && user?.email === data?.account?.email) {
                return;
            }
            // Only when user is not logged in, login to cortex
            await iamClient.get(`/v1/sso/token?aud=${getCortexEndpointUrl(env)}`, {
                headers: {
                    'X-SVC-Version': undefined,
                },
                withCredentials: true,
            });
        }
        catch (error) {
            console.error('Error logging in to Cortex:', error);
        }
    }, [iamClient, user?.email]);
    const triggerCortexLogin = useCallback(async () => {
        if (isLoadingEnvironments || isCortexLoginSettled || cortexLoginInProgressRef.current) {
            return;
        }
        cortexLoginInProgressRef.current = true;
        try {
            if (env === 'test' && selectedTestEnv.value) {
                const foundEnv = devEnvironments.find((envItem) => envItem.cortexId === selectedTestEnv.value);
                if (foundEnv) {
                    await loginToCortex(foundEnv);
                }
            }
            if (env === 'prod' && hasProductionEnvironments) {
                const loginPromises = prodEnvironments.map((environment) => loginToCortex(environment));
                await Promise.all(loginPromises);
            }
        }
        catch (error) {
            console.error('Error triggering Cortex login:', error);
        }
        finally {
            cortexLoginInProgressRef.current = false;
            setIsCortexLoginSettled(true);
        }
    }, [
        isLoadingEnvironments,
        isCortexLoginSettled,
        env,
        selectedTestEnv.value,
        devEnvironments,
        hasProductionEnvironments,
        prodEnvironments,
        loginToCortex,
    ]);
    const fetchAuthPolicies = useCallback(async (targetStudyId) => {
        const effectiveStudyId = targetStudyId || studyId;
        if (!user?.id || !entityId || !effectiveStudyId) {
            return;
        }
        try {
            const { data } = await iamClient.get(`/v1/identities/${user?.id}/policies?resource=entity/${entityId}/study/${effectiveStudyId}&includeOwn=true`);
            const policies = data?.data?.map((policy) => policy.role) ?? [];
            setAuthPolicies(policies);
        }
        catch (error) {
            console.error('Error fetching auth policies:', error);
        }
    }, [iamClient, entityId, studyId, user?.id]);
    const hasPolicy = useCallback((policy) => {
        return authPolicies.includes(policy);
    }, [authPolicies]);
    const value = useMemo(() => ({
        activeReportsTab,
        setActiveReportsTab,
        drilldownLevel,
        setDrilldownLevel,
        libRequestsParams,
        selectedTestEnv,
        setSelectedTestEnv,
        devEnvironments,
        prodEnvironments,
        isInitialized,
        isLoadingEnvironments,
        hasProductionEnvironments,
        setStudyId,
        testEnvironmentsOptions,
        triggerCortexLogin,
        setIsCortexLoginSettled,
        fetchAuthPolicies,
        authPolicies,
        hasPolicy,
    }), [
        activeReportsTab,
        setActiveReportsTab,
        drilldownLevel,
        setDrilldownLevel,
        libRequestsParams,
        selectedTestEnv,
        setSelectedTestEnv,
        devEnvironments,
        prodEnvironments,
        isInitialized,
        isLoadingEnvironments,
        hasProductionEnvironments,
        setStudyId,
        testEnvironmentsOptions,
        triggerCortexLogin,
        setIsCortexLoginSettled,
        fetchAuthPolicies,
        hasPolicy,
    ]);
    return _jsx(DataReportsContext.Provider, { value: value, children: children });
};
